import { useCallback, useContext, useEffect, useState } from 'react';

import * as AssociadoVinculoAPI from '../../../api/associadoVinculo';
import * as EmpresaAPI from '../../../api/empresa';
import { Context } from '../../../context/UserContext';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import styles from '../../form/Form.module.css';

import Form from './Form';

function criarEstadoInicial(empresaId) {
    return {
        empresaId: Number(empresaId) || '',
        pessoaId: null,
        Pessoa: {
            descricao: '',
        },
        dataInicio: '',
        dataFim: '',
        motivoDesligamento: '',
        ativo: true,
        observacao: '',
    };
}

function CreateOrEdit() {
    const { parametro } = useContext(Context);
    const { empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const pathname = '/AssociadoVinculos';
    const urlParams = new URLSearchParams(window.location.search);

    const uuid = urlParams.get('uuid');
    const editando = Boolean(uuid && uuid !== 'null');

    const [token] = useState(localStorage.getItem('token') || '');
    const [dados, setDados] = useState(() => criarEstadoInicial(empresaId));
    const [optionsEmpresa, setOptionsEmpresa] = useState([]);
    const [carregando, setCarregando] = useState(editando);
    const [salvando, setSalvando] = useState(false);
    const [falhaCarregamento, setFalhaCarregamento] = useState(false);

    const carregarVinculo = useCallback(async () => {
        if (!editando || !uuid) return null;

        setCarregando(true);
        setFalhaCarregamento(false);

        try {
            const response = await AssociadoVinculoAPI.consultar(uuid);
            const vinculo = response.data?.data || response.data;

            setDados((anterior) => ({
                ...anterior,
                ...vinculo,
                empresaId: Number(vinculo.empresaId) || '',
                pessoaId: Number(vinculo.pessoaId) || null,
                Pessoa: vinculo.Pessoa || { descricao: '' },
                dataInicio: vinculo.dataInicio ? String(vinculo.dataInicio).slice(0, 10) : '',
                dataFim: vinculo.dataFim ? String(vinculo.dataFim).slice(0, 10) : '',
                motivoDesligamento: vinculo.motivoDesligamento || '',
                observacao: vinculo.observacao || '',
            }));

            return vinculo;
        } catch (error) {
            setFalhaCarregamento(true);
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível carregar o vínculo do associado.',
                error.response?.data?.type || 'error'
            );

            return null;
        } finally {
            setCarregando(false);
        }
    }, [editando, setFlashMessage, uuid]);

    const consultarEmpresas = useCallback(async () => {
        const result = await EmpresaAPI.empresa();

        setOptionsEmpresa(result);
        return result;
    }, []);

    useEffect(() => {
        consultarEmpresas();
    }, [consultarEmpresas]);

    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
        }
    }, [navigate, token]);

    useEffect(() => {
        carregarVinculo();
    }, [carregarVinculo]);

    async function salvarVinculo(novosDados) {
        if (salvando) return;

        const pessoaId = Number(novosDados.pessoaId);
        const dataInicio = String(novosDados.dataInicio || '').trim();
        const dataFim = String(novosDados.dataFim || '').trim() || null;

        if (!Number.isInteger(pessoaId) || pessoaId <= 0) {
            setFlashMessage('Selecione uma pessoa válida.', 'warning');
            return;
        }

        if (!dataInicio) {
            setFlashMessage('Informe a data de início da associação.', 'warning');
            return;
        }

        if (dataFim && dataFim < dataInicio) {
            setFlashMessage('A data de término não pode ser anterior à data de início.', 'warning');
            return;
        }

        const payload = {
            empresaId: Number(novosDados.empresaId),
            pessoaId,
            dataInicio,
            dataFim,
            motivoDesligamento: dataFim ? String(novosDados.motivoDesligamento || '').trim() || null : null,
            ativo: Boolean(novosDados.ativo),
            observacao: String(novosDados.observacao || '').trim() || null,
        };

        setSalvando(true);

        try {
            const response = editando
                ? await AssociadoVinculoAPI.editar(uuid, payload)
                : await AssociadoVinculoAPI.cadastrar(payload);

            setFlashMessage(response.data.message, response.data.type);

            const sucesso = response.status >= 200 && response.status < 300 && response.data?.type === 'success';

            if (!sucesso) return;

            const params = new URLSearchParams(urlParams);

            params.delete('uuid');
            params.delete('btnText');

            navigate(pathname, params);
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || error.message || 'Não foi possível salvar o vínculo do associado.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setSalvando(false);
        }
    }

    return (
        <section className={`${styles.form_container} ${styles[parametro.tema]}`}>
            <Form
                dados={dados}
                setDados={setDados}
                handleSubmit={salvarVinculo}
                caminhoVoltar={pathname}
                OptionsEmpresa={optionsEmpresa}
                editando={editando}
                carregando={carregando}
                salvando={salvando}
                falhaCarregamento={falhaCarregamento}
            />
        </section>
    );
}

export default CreateOrEdit;
